import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {Device, DeviceCart, DeviceFeature, OrderHistory, Product, Role, User} from '@kion/data-models';

// tslint:disable-next-line:nx-enforce-module-boundaries

// tslint:disable-next-line:nx-enforce-module-boundaries

import {TranslateService} from '@ngx-translate/core';
import {SubSink} from "subsink";
import {CorrelationObserverService} from "@kion/ngrx-helper";
import {debounceTime, filter, map, switchMap, tap} from "rxjs/operators";
import {ActivatedRoute} from "@angular/router";
import {DevicesFacade} from "@kion/data-access/data-access-devices";
import {RolesFacade} from "@kion/data-access/data-access-roles";
import {UsersFacade} from "@kion/data-access/data-access-users";
import {OrderHistoryFacade} from "@kion/data-access/data-access-orderhistory";
import {ToastService} from "@kion/ui";

@Component({
    selector: 'kion-cms-order-history-detail',
    templateUrl: './order-history-detail.component.html',
    styleUrls: ['./order-history-detail.component.scss']
})
export class OrderHistoryDetailComponent implements OnInit, OnDestroy {
    private orderId: string;

    orderHistory$: Observable<OrderHistory>;
    device$: Observable<Device>;
    deviceCart$ = new BehaviorSubject<DeviceCart | null>(null);
    features$: Observable<DeviceFeature[]>;
    roles$: Observable<Role[]>;
    currentUser$: Observable<User>;
    canEdit$: Observable<boolean>;
    triggerPoDataChange$: Subject<[string, string]> = new Subject<[string, string]>();

    private readonly subSink = new SubSink();

    constructor(
        private route: ActivatedRoute,
        private observer: CorrelationObserverService,
        private devicesFacade: DevicesFacade,
        private orderHistoryFacade: OrderHistoryFacade,
        private translationService: TranslateService,
        private rolesFacade: RolesFacade,
        private usersFacade: UsersFacade,
        private toastMessageService: ToastService,
    ) {
    }


    ngOnInit(): void {

        this.currentUser$ = this.usersFacade.all$.pipe(
            map((users) => users.find((user) => user.id === '1'))
        );

        //Load Roles by User
        this.subSink.sink = this.currentUser$.subscribe((user) => {
            this.rolesFacade.loadByUserId(user.uid, true);
        });
        this.orderHistory$ = this.orderHistoryFacade.all$.pipe(
            map((orders: OrderHistory[]) => orders.find((order: OrderHistory) => order.id === this.orderId)),
            filter((orderHistory) => !!orderHistory),
        );

        this.device$ = combineLatest(
            [
                this.devicesFacade.all$,
                this.orderHistory$
            ]
        ).pipe(
            filter(([, order]) => !!order && !!(order.device)),

            tap(([, order]) => {
                //DeviceCart
                this.deviceCart$.next({
                    entries: order.deviceFeatures
                } as unknown as DeviceCart);

            }),
            map(
                ([devices, order]) => {
                    const device = devices.find(
                        d => d.serialNumber === order.device.serialNumber
                            && d.manufacturer === order.device.manufacturer
                    );

                    //TODO: Correct data types (currently workaround by mixing them up)
                    const products = order.deviceFeatures?.map((entry: any) => entry?.product) ?? [] as DeviceFeature[];
                    if(!device){
                        return {...order.device, products: products as Product[]} as Device;
                    }
                    else{
                        return device;
                    }
                }
            ),
            filter((device) => !!device),
        );

        this.features$ = this.orderHistory$.pipe(
            map((orderHistory) => orderHistory.deviceFeatures),
            filter((features) => !!features),
        );

        this.roles$ = combineLatest([this.orderHistory$, this.rolesFacade.all$]).pipe(
          filter(([orderHistory]) => !!orderHistory),
          map(([orderHistory, roles]) => {
             return roles.filter(
                (role: Role) => role.baseStore === orderHistory.deviceOwner.networkPartnerId
             );
          }),
        );

        this.subSink.sink = this.orderHistory$.subscribe((order) => this.devicesFacade.loadById(order.device.manufacturer, order.device.serialNumber));

        this.canEdit$ = combineLatest([this.roles$, this.orderHistory$, this.currentUser$]).pipe(
            filter(([roles, orderHistroy, user]) => !!roles && !!orderHistroy && !!user),
            map(([roles, orderhistory, user]) => {
                const rolesToCheck = ['fsNwpAdmin', 'fsAdmin', 'fsNwpApprover'];
                if (roles.some(role => rolesToCheck.includes(role.role.uid))) {
                    return true;
                } else if (orderhistory.buyer.uid === user.uid) {
                    return true;
                }
                return false;
            })
        );

        this.orderId = this.route.snapshot.params.id;
        this.orderHistoryFacade.loadOrderHistoryById(this.orderId);

        this.orderHistory$.pipe(
            switchMap((orderHistory) =>
                this.triggerPoDataChange$.pipe(
                    debounceTime(500),
                    map(([poNumber, poNote]) => {
                        this.subSink.sink = this.observer.subscribeToAction(
                            this.orderHistoryFacade.update(orderHistory?.id, poNote, poNumber),
                            () =>
                                this.toastMessageService.show(
                                    {
                                        text: this.translationService.instant('admin.toast.orderHistory.update.success'),
                                        type: 'success',
                                    }
                                ),
                            () => this.toastMessageService.show(
                                {
                                    text: this.translationService.instant('admin.toast.orderHistory.update.error'),
                                    type: 'error',
                                }
                            ),
                        );
                        return orderHistory;
                    })
                ))).subscribe();
    }

    poformChange = (data: [string, string]) => {
        this.triggerPoDataChange$.next(data);
    }

    ngOnDestroy() {
        this.subSink.unsubscribe();
    }
}
